/**
 * nudges.ts — Nudge dismiss + snooze JS — Phase 171
 *
 * Dismiss: POST id to data-url, fade+remove card on success.
 * Snooze: expand inline State C picker; POST id+days on duration pick, fade+remove on success.
 * "Not now" restores original action buttons without any POST.
 *
 * After card removal: if 0 .js-nudge-card remain, swap active section to calm-day empty state.
 *
 * All mutations POST to signal URLs read from btn.dataset.url / btn.dataset.snoozeUrl
 * (never hardcode ?do=).
 * fetch POST uses new URLSearchParams() + Content-Type: application/x-www-form-urlencoded.
 * Bootstrap is NOT on window — not imported here (no Bootstrap components used in this module).
 */

// Calm-day empty-state HTML injected after last card is removed (D-03)
const CALM_STATE_HTML = `
<div class="text-center py-5" id="nudges-calm-state">
    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 512 512" fill="currentColor" aria-hidden="true" class="text-muted mb-3" style="color:#00B894 !important"><path d="M256 48a208 208 0 1 1 0 416A208 208 0 0 1 256 48zm0-48C114.6 0 0 114.6 0 256S114.6 512 256 512s256-114.6 256-256S397.4 0 256 0zM363.8 346.2c-4.4-6-11.3-9.8-18.7-9.2-35.4 2.9-72.5 2.9-89.1 2.9s-53.7 0-89.1-2.9c-7.4-.6-14.3 3.2-18.7 9.2s-4.9 14.3-1.1 20.8C161.1 398.1 204.7 416 256 416s94.9-17.9 108.9-49c3.8-6.5 3.3-14.8-1.1-20.8zM176.4 272a32 32 0 1 0 0-64 32 32 0 1 0 0 64zm159.2 0a32 32 0 1 0 0-64 32 32 0 1 0 0 64z"/></svg>
    <h5 class="fw-bold mt-2">All good</h5>
    <p class="text-muted small">Nothing needs your attention right now \u{1F49A}</p>
</div>`;

export default {

    _inited: false,

    init(): void {
        // Idempotent init guard — prevent double-bind if init() is called twice
        if (this._inited) return;

        // Only activate on the /nudges page (cards container present)
        const activeSection = document.getElementById('nudges-active-section');
        if (!activeSection) return;

        this._inited = true;

        // ── Dismiss buttons ──────────────────────────────────────────────────
        activeSection.addEventListener('click', (e: Event) => {
            const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.js-dismiss-nudge');
            if (btn) {
                e.preventDefault();
                this._dismissNudge(btn);
            }
        });

        // ── Snooze button (show picker) ───────────────────────────────────────
        activeSection.addEventListener('click', (e: Event) => {
            const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.js-snooze-nudge');
            if (btn) {
                e.preventDefault();
                this._openSnoozePicker(btn);
            }
        });

        // ── Snooze apply (duration pick) ─────────────────────────────────────
        activeSection.addEventListener('click', (e: Event) => {
            const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.js-snooze-apply');
            if (btn) {
                e.preventDefault();
                this._applySnooze(btn);
            }
        });

        // ── Snooze cancel (Not now) ──────────────────────────────────────────
        activeSection.addEventListener('click', (e: Event) => {
            const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.js-snooze-cancel');
            if (btn) {
                e.preventDefault();
                const card = btn.closest<HTMLElement>('.js-nudge-card');
                if (card) this._closeSnoozePicker(card);
            }
        });
    },

    // ── Dismiss ──────────────────────────────────────────────────────────────

    _dismissNudge(btn: HTMLButtonElement): void {
        const id  = btn.dataset.nudgeId ?? '';
        const url = btn.dataset.url ?? '';
        if (!id || !url) return;

        const card = btn.closest<HTMLElement>('.js-nudge-card');
        const body = new URLSearchParams();
        body.set('id', id);

        fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })
        .then(r => r.json())
        .then((data: any) => {
            if (data.success && card) {
                this._fadeAndRemove(card);
            }
        })
        .catch(() => { /* silent fail — card stays */ });
    },

    // ── Snooze ───────────────────────────────────────────────────────────────

    _openSnoozePicker(btn: HTMLButtonElement): void {
        const card = btn.closest<HTMLElement>('.js-nudge-card');
        if (!card) return;
        const actions = card.querySelector<HTMLElement>('.js-nudge-actions');
        const picker  = card.querySelector<HTMLElement>('.js-snooze-picker');
        if (actions) actions.classList.add('d-none');
        if (picker)  picker.classList.remove('d-none');
    },

    _closeSnoozePicker(card: HTMLElement): void {
        const actions = card.querySelector<HTMLElement>('.js-nudge-actions');
        const picker  = card.querySelector<HTMLElement>('.js-snooze-picker');
        if (picker)  picker.classList.add('d-none');
        if (actions) actions.classList.remove('d-none');
    },

    _applySnooze(btn: HTMLButtonElement): void {
        const id   = btn.dataset.nudgeId ?? '';
        const url  = btn.dataset.snoozeUrl ?? '';
        const days = btn.dataset.days ?? '';
        if (!id || !url || !days) return;

        const card = btn.closest<HTMLElement>('.js-nudge-card');
        const body = new URLSearchParams();
        body.set('id', id);
        body.set('days', days);

        fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })
        .then(r => r.json())
        .then((data: any) => {
            if (data.success && card) {
                this._fadeAndRemove(card);
            }
        })
        .catch(() => { /* silent fail — card stays */ });
    },

    // ── Shared helpers ────────────────────────────────────────────────────────

    _fadeAndRemove(card: HTMLElement): void {
        card.style.transition = 'opacity 0.3s';
        card.style.opacity = '0';
        setTimeout(() => {
            card.remove();
            this._checkCalmState();
        }, 350);
    },

    _checkCalmState(): void {
        // If 0 active nudge cards remain, swap to calm-day empty state without reload
        const activeSection = document.getElementById('nudges-active-section');
        if (!activeSection) return;

        const remaining = activeSection.querySelectorAll('.js-nudge-card');
        if (remaining.length === 0) {
            activeSection.innerHTML = CALM_STATE_HTML;
        }
    },
};
